import _ from 'underscore';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import getConfig from 'next/config';

import FavoritesButton from 'components/FavoritesButton';
import PrintButton from 'components/PrintButton';
import ShareButtons from 'components/ShareButtons';

import { PVI_NO_STYLES_CLASSNAME } from 'utils/helpers/inclusion';
import { useTabNavigation } from 'hooks/useTabNavigation';
import styles from './styles.module.scss';

const { publicRuntimeConfig } = getConfig();

const actionsMarginTop = 64;

const ActionContainer = ({
	entity,
	entityType,
	parentContainerRef,
	hasTopPromoBlock,
	isStaticPosition,
	customSocialButtonsOrder
}) => {
	const containerRef = useRef(null);
	const [position, setPosition] = useState(null);
	const [actionsOffsetTop, setActionsOffsetTop] = useState(0);

	useEffect(() => {
		if (!isStaticPosition) {
			const onScroll = () => {
				const currentPosition = window.scrollY || window.pageYOffset;

				const {
					offsetTop: parentTopPosition = 0,
					offsetHeight: parentHeight = 0
				} = parentContainerRef.current || {};

				const isAboveParentContainer = parentTopPosition - 33 > currentPosition;

				if (isAboveParentContainer) {
					setPosition(null);
				} else {
					const { offsetHeight } = containerRef.current;

					const isBottomPosition =
						currentPosition + offsetHeight + 80 >=
						parentTopPosition + parentHeight - 100;

					setPosition(isBottomPosition ? 'bottom' : 'fixed');
				}
			};

			onScroll();

			window.addEventListener('scroll', onScroll);

			return () => window.removeEventListener('scroll', onScroll);
		}

		return null;
	}, []);

	useEffect(() => {
		if (!isStaticPosition) {
			const onOffsetActions = () => {
				const bodyScrollTop = document.documentElement.scrollTop;
				if (hasTopPromoBlock) {
					if (
						bodyScrollTop > publicRuntimeConfig.features.topPromoBlockHeight
					) {
						setActionsOffsetTop(
							actionsMarginTop -
								publicRuntimeConfig.features.topPromoBlockHeight
						);
					} else {
						setActionsOffsetTop(actionsMarginTop - bodyScrollTop);
					}
				} else {
					setActionsOffsetTop(actionsMarginTop);
				}
			};
			onOffsetActions();

			window.addEventListener('scroll', onOffsetActions);

			return () => window.removeEventListener('scroll', onOffsetActions);
		}

		return null;
	}, [hasTopPromoBlock]);

	const actionContainerCx = cx(
		styles.container,
		styles[`position_${position}`],
		{
			[styles.container_static]: isStaticPosition
		}
	);
	const actionBlockStatic = cx(styles.block, {
		[styles.block_static]: isStaticPosition
	});

	const showPrintButton = ['poems'].includes(entityType);

	const { handleFocus, handleKeyDown, listItemTabIndex } = useTabNavigation();

	return (
		<div
			className={actionContainerCx}
			ref={containerRef}
			style={!isStaticPosition ? { marginTop: actionsOffsetTop } : null}
			tabIndex={0}
			aria-label="Поделиться в соц-сетях"
			role="toolbar"
			aria-orientation="horizontal"
			onFocus={handleFocus}
			onKeyDown={handleKeyDown}
		>
			{isStaticPosition && <div className={styles.text}>Поделиться:</div>}
			<div className={actionBlockStatic}>
				<ShareButtons
					{...{ customSocialButtonsOrder }}
					theme={isStaticPosition ? 'horizontal' : null}
					tabIndex={listItemTabIndex}
				/>
			</div>
			{!_(entity).isEmpty() && entityType && (
				<div className={actionBlockStatic}>
					<FavoritesButton
						entityType={entityType}
						entityId={entity._id}
						title={entity.title}
						className={styles.button}
						iconClassName={cx(
							styles.icon,
							styles.iconFavorite,
							PVI_NO_STYLES_CLASSNAME
						)}
						tabIndex={listItemTabIndex}
					/>
				</div>
			)}
			{showPrintButton && (
				<div className={actionBlockStatic}>
					<PrintButton
						className={styles.button}
						entityType={entityType}
						iconClassName={cx(styles.icon, styles.iconPrint)}
						tabIndex={listItemTabIndex}
					/>
				</div>
			)}
		</div>
	);
};

ActionContainer.propTypes = {
	entity: PropTypes.shape({
		_id: PropTypes.number,
		title: PropTypes.string
	}),
	entityType: PropTypes.string,
	parentContainerRef: PropTypes.shape({
		current: PropTypes.object
	}),
	hasTopPromoBlock: PropTypes.bool,
	isStaticPosition: PropTypes.bool,
	customSocialButtonsOrder: PropTypes.string
};

ActionContainer.defaultProps = {
	entity: null,
	entityType: null
};

export default ActionContainer;
